/**
 * Acción de REDUX que permite llevar a cabo todas las operaciones sobre las carpetas, archivos y buscador 
 */

import { isMember } from '../../lib/customer_links';
import { toGranular } from '../../lib/permission_granular';
import { DOCUMENTS } from '../../../application/types';
import { recordToTrash, extractCustomerId } from './trash';
import { sanitizeForFirestore } from '../../lib/firestore_safe';
import { toAccountingList, removeAccountingRef } from '../../lib/accounting_refs';
import { canonicalUserName } from '../../lib/user_name';
import { FILES_SUBCOLLECTION } from '../../lib/folder_files';
import { denormalizeFileFlagsToParent } from '../../lib/denormalize_file_flags';
import { buildAttachmentTrashEntry } from '../../lib/attachment_trash';
import { buildPermissionsAudit, originOfMatrixPath } from '../../lib/permission_audit';
import { canAdjust, canRestrict, canToggleDelete, diffTouchesDelete } from '../../lib/rank';
import { createEffectiveLoader, filterSearchResults } from '../../lib/effective_loader';
import { loadCompanyAccess } from '../../lib/company_access';

/**
 * Deja en la papelera un adjunto del cajón (contabilización, XML o
 * vinculado) DENTRO del batch del borrado: o se borra y queda restaurable, o
 * no pasa nada. Antes estas tres acciones borraban sin papelera y lo borrado
 * solo se recuperaba a mano desde el audit (ver lib/attachment_trash.js).
 */
const addAttachmentToTrash = ( batch, firebase, { kind, attachmentRef, attachmentData, parentRef, parentData, requestedBy } ) => {
  const db = firebase().firestore();
  const user = firebase().auth().currentUser;
  const entry = buildAttachmentTrashEntry({
    kind,
    attachmentPath: attachmentRef.path,
    attachmentData: sanitizeForFirestore( attachmentData || {} ),
    parentPath: parentRef.path,
    parentName: parentData?.name,
    customerId: extractCustomerId( parentData?.folder?.path ),
    uid: user?.uid,
    userName: canonicalUserName( undefined, user ),
    requestedBy,
  });
  batch.set( db.collection('trash').doc(), {
    ...entry,
    deletedAt: firebase().firestore.FieldValue.serverTimestamp(),
  });
};

/**
 * Borra UNA matriz de permisos con auditoría 'delete'. `expectedOrigin`
 * ('carpeta' | 'empresa', según lib/permission_audit) evita borrar la matriz
 * equivocada: la de la empresa nunca se quita desde una carpeta.
 * keepCarpetas: si la matriz trae abrir/cerrar decidido por root
 * (permissions.carpetas), no se borra.
 */
const clearMatrix = ({ roles, matrixRef, uid, expectedOrigin, keepCarpetas = false }) => ( dispatch, state, firebase ) => {

  dispatch({ type: DOCUMENTS.UPDATE.UPDATING_DOCUMENTS });

  return new Promise( async ( resolve, reject ) => {
    if ( !roles?.update || !matrixRef || !uid ) {
      return reject(
        dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: 'Permisos insuficientes o faltan datos para quitar los permisos propios' })
      );
    }
    if ( originOfMatrixPath( matrixRef.path ) !== expectedOrigin ) {
      return reject(
        dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: expectedOrigin === 'carpeta'
          ? 'No se puede quitar la matriz de la empresa desde una carpeta.'
          : 'La matriz indicada no es la de la empresa.' })
      );
    }
    try {
      const db = firebase().firestore();
      const snap = await matrixRef.get();
      if ( !snap.exists ) {
        return resolve( dispatch({ type: DOCUMENTS.UPDATE.UPDATED_DOCUMENTS, data: matrixRef }) );
      }
      const before = snap.data();
      if ( keepCarpetas && Object.keys( before?.permissions?.carpetas || {} ).length ) {
        return reject(
          dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: 'Esta empresa tiene «Abrir y cerrar carpetas» definido por root: solo root puede restablecer estos permisos.' })
        );
      }
      const batch = db.batch();
      const log_audit = db.collection('audit').doc();
      batch.set(log_audit, {
        ...buildPermissionsAudit({
          eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_PERMISSIONS`,
          actorUid: firebase().auth().currentUser.uid,
          targetUid: uid,
          matrixPath: matrixRef.path,
          before,
          action: 'delete',
        }),
        eventDate: firebase().firestore.FieldValue.serverTimestamp(),
      });
      batch.set(log_audit.collection('before').doc(), before);
      batch.delete(matrixRef);
      await batch.commit();
      return resolve( dispatch({ type: DOCUMENTS.UPDATE.UPDATED_DOCUMENTS, data: matrixRef }) );
    } catch ( error ) {
      return reject( dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: error.code, message: error.message }) );
    }
  });

};

/**
 * Exporta las funciones para el manejo de las carpetas
 */
export const documents = {

  /**
   * Función que permite crear una carpeta en el sistema.
   * Recibe como parametros:
   * La referencia de la base de datos en donde va a hacer creada la carpeta 
   * Roles del usuario que esta realizando la operación
   * Información de la carpeta que se va a crear
   */
  create: ({ reference, roles, folder }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.CREATE.CREATING_DOCUMENTS });

    return new Promise( ( resolve, reject ) => {
      if ( roles.create ) {
        reference.get().then( fields => {
          
          reference = fields.data ? reference : reference.doc(window.btoa( Math.floor( Date.now() * Math.random() ).toString()));

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();

          const log_audit = db.collection('audit').doc();
          const doc = reference.collection(window.btoa( Math.floor( Date.now() * Math.random() ).toString()));
          const eventType = `${ DOCUMENTS.CREATE.CREATE_DOCUMENTS }${ reference.path.includes('templates/folders') ? '_TEMPLATES' : '' }`;

          const after = {
            name: folder.name,
            description: folder.description,
            isUpload: folder.isUpload,
            // Fecha de creación (cliente, no serverTimestamp: Firestore no
            // permite serverTimestamp dentro de arrayUnion). Carpetas viejas
            // no tienen este campo — la UI lo muestra solo si existe.
            createdAt: new Date(),
            reference: {
              path: doc.parent,
              id: doc.id
            }
          };

          batch.set(log_audit, { uid, eventType, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit.collection('after').doc(), after);
          batch[fields.exists ? 'update' : 'set'](reference, { folders: firebase().firestore.FieldValue.arrayUnion(after) });
          batch.commit().then(() => {
            resolve(
              dispatch({ type: DOCUMENTS.CREATE.CREATED_DOCUMENTS, data: doc })
            )
          }).catch(
            error => reject(
              dispatch({ type: DOCUMENTS.CREATE.ERROR, code: error.code, message: error.message })
            )
          );

        }).catch( error => reject( dispatch({ type: DOCUMENTS.READ.ERROR, code: error.code, message: error.message }) ))

      } else {
        reject(
          dispatch({ type: DOCUMENTS.CREATE.ERROR, code: 0, message: 'Permisos insuficientes para crear documentos' })
        )
      }
    })

  },

  /**
   * Función que permite consultar las carpetas para un cliente especifico.
   * Recibe como parametros:
   * El ID del cliente 
   * Roles del usuario que esta realizando la operación
   */
  read: ({ uid, roles }) => ( dispatch, state, firebase ) => {
  
    dispatch({ type: DOCUMENTS.READ.READING_DOCUMENTS });
  
    return new Promise( ( resolve, reject ) => {
      if ( roles.read ) {
       
        const db = firebase().firestore();
        const documents = db.collection('folders').doc( uid );
  
        documents.get().then( query => resolve(
          dispatch({
            type: DOCUMENTS.READ.READED_DOCUMENTS,
            data: query.ref
          })
        )).catch( error => reject(
          dispatch({
            type: DOCUMENTS.READ.ERROR,
            code: error.code,
            message: error.message
          })
        ))
  
      } else {
        reject(
          dispatch({ type: DOCUMENTS.READ.ERROR, code: 0, message: 'Permisos insuficientes para consultar la lista de documentos' })
        )
      }
    });
  
  },

  /**
   * Función que permite actualizar una carpeta para un cliente especifico.
   * Recibe como parametros:
   * Información de la carpeta antes de ser modificada con la referencia en la base de datos
   * Roles del usuario que esta realizando la operación
   * Información actualizada de la carpeta
   */
  update: ({ item, roles, values }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.UPDATE.UPDATING_DOCUMENTS });

    return new Promise( ( resolve, reject ) => {
      if ( roles.update ) {

        item.reference.path.get().then( fields => {

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();
          const log_audit = db.collection('audit').doc();
          
          const folders = fields.data().folders;
          const index = folders.findIndex( folder => folder.name === item.name );
          const eventType = `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }${ fields.ref.path.includes('templates/folders') ? '_TEMPLATES' : '' }`;

          const before = { ...folders[ index ] };

          folders[ index ].name = values.name;
          folders[ index ].description = values.description;
          folders[ index ].isUpload = values.isUpload;

          batch.set(log_audit, { uid, eventType, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit.collection('before').doc(), before);
          batch.set(log_audit.collection('after').doc(), folders[ index ]);
          batch.update(fields.ref, { folders });

          return batch.commit().then(() => {
            resolve(
              dispatch({ type: DOCUMENTS.UPDATE.UPDATED_DOCUMENTS, data: fields.ref })
            )
          }).catch(
            error => reject(
              dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: error.code, message: error.message })
            )
          );

        }).catch( error => reject(
          dispatch({
            type: DOCUMENTS.UPDATE.ERROR,
            code: error.code,
            message: error.message
          })
        ))

      } else {
        reject(
          dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: 'Permisos insuficientes para actualizar la lista de documentos' })
        )
      }
    })

  },

  /**
   * Función que permite eliminar una carpeta para un cliente especifico.
   *
   * Esta versión soporta eliminación RECURSIVA: si la carpeta tiene
   * sub-carpetas y/o archivos, se elimina toda la jerarquía. El árbol
   * completo se registra en la papelera (`trash`) con todos sus descendientes
   * para poder restaurarlo, y se crea una entrada de audit con el snapshot.
   *
   * Recibe como parametros:
   * - roles                  Roles del usuario que realiza la operación.
   * - item                   { name, reference: { path, id }, token? }
   * - isValidateToken        Si true, el token ya fue validado server-side
   *                          (Cloud Function); habilita el borrado en cascada
   *                          (se omite la regla de "solo carpetas vacías").
   */
  delete: ({ roles, item, isValidateToken = false }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.DELETE.DELETING_DOCUMENTS });
    return new Promise( async ( resolve, reject ) => {
      if ( !roles.delete ) {
        return reject(
          dispatch({ type: DOCUMENTS.DELETE.ERROR, code: 0, message: 'Permisos insuficientes para eliminar la lista de documentos' })
        );
      }

      // ── Cascada con token → job en backend ─────────────────────────
      // El borrado grande ya no corre en el navegador: la callable
      // startFolderDeletion valida el token, quita el entry del padre en
      // segundos y un worker resumible borra/respalda el árbol en Cloud
      // Functions. El progreso se sigue por deletion_jobs/{jobId}.
      if (isValidateToken) {
        try {
          const parentPath = typeof item.reference?.path === 'string'
            ? item.reference.path
            : item.reference?.path?.path;
          if (!parentPath) throw new Error('No se pudo determinar la ruta de la carpeta. Recarga e intenta de nuevo.');
          const customerId = extractCustomerId(parentPath);
          const callable = firebase().app().functions().httpsCallable('startFolderDeletion');
          const res = await callable({
            parentPath,
            folderName: item.name,
            token: item.token || '',
            customerId,
          });
          return resolve(dispatch({
            type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
            jobId: res.data?.jobId || null,
            trashId: res.data?.trashId || null,
          }));
        } catch (error) {
          dispatch({ type: DOCUMENTS.DELETE.ERROR, code: error.code, message: error.message });
          return reject(error);
        }
      }

      try {
        const db = firebase().firestore();

        // ── Token ya validado server-side (Cloud Function validateDeletionToken) ──
        // El flag `isValidateToken` ahora solo significa "autorizado por token":
        // habilita el borrado en cascada (se omite la regla de "solo vacías").
        // La validación del valor del token ya ocurrió en el cliente antes de
        // despachar esta acción, contra la callable; aquí no se relee `tokens`
        // (un usuario normal ya no puede leer esa colección).

        // ── Localizar la entry en el padre ─────────────────────────────
        const parentSnap = await item.reference.path.get();
        const parentFolders = parentSnap.data ? parentSnap.data()?.folders : undefined;

        if (!Array.isArray(parentFolders) || parentFolders.length === 0) {
          // Fallback: eliminar el doc del id (comportamiento legacy)
          await item.reference.path.doc(item.reference.id).delete();
          return resolve(dispatch({
            type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
            data: item
          }));
        }

        const index = parentFolders.findIndex(f => f.name === item.name);
        if (index < 0) {
          return reject(dispatch({
            type: DOCUMENTS.DELETE.ERROR,
            code: 0,
            message: 'La carpeta no se encontró en el padre. Recarga e intenta de nuevo.'
          }));
        }
        const folderEntry = parentFolders[index];

        // ── Política: solo se eliminan carpetas VACÍAS (sin token) ─────
        // Regla del negocio (instrucción del Ing. Ramiro): evitar borrado
        // accidental de árboles completos. Si la carpeta tiene descendientes
        // (sub-carpetas o archivos), se rechaza el delete y se exige vaciar.
        //
        // EXCEPCIÓN: si la operación se confirmó con token (isValidateToken),
        // se permite el borrado en cascada — la papelera (recordToTrash, más
        // abajo) respalda el árbol completo para restaurar en caso de error.
        if (!isValidateToken && folderEntry?.reference?.id) {
          try {
            const checkSnap = await parentSnap.ref.collection(folderEntry.reference.id).limit(1).get();
            if (!checkSnap.empty) {
              return resolve(dispatch({
                type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
                code: -98,
                message: 'No se puede eliminar la carpeta porque tiene contenido. Elimine primero los archivos y subcarpetas internos.'
              }));
            }
          } catch (e) {
            console.warn('No se pudo verificar contenido de la carpeta:', e?.message);
          }
        }

        // ── Recopilación recursiva del árbol descendiente ──────────────
        // descendants: docs bajo `folders/{cid}/...` (sub-colecciones)
        // legacyFiles: docs en colección `files/` apuntados por `reference`
        const descendants = []; // [{ path, data }]
        const legacyFiles = []; // [{ path, data }]

        const gatherFolderRecursive = async (parentRef, fEntry) => {
          if (!fEntry?.reference?.id) return;
          const subCol = parentRef.collection(fEntry.reference.id);
          let docsSnap;
          try {
            docsSnap = await subCol.get();
          } catch (e) {
            console.warn('No se pudo leer sub-colección:', e?.message);
            return;
          }
          for (const doc of docsSnap.docs) {
            const data = doc.data() || {};
            descendants.push({ path: doc.ref.path, data });

            // Recursivo: cada entry en data.folders → otra sub-colección
            if (Array.isArray(data.folders)) {
              for (const subF of data.folders) {
                await gatherFolderRecursive(doc.ref, subF);
              }
            }

            // Archivo legacy: doc tiene `reference` apuntando a files/{id}
            if (data.reference && typeof data.reference.get === 'function') {
              try {
                const fileDoc = await data.reference.get();
                if (fileDoc.exists) {
                  legacyFiles.push({ path: fileDoc.ref.path, data: fileDoc.data() });
                }
              } catch (e) { /* archivo legacy inaccesible */ }
            }
          }
        };

        await gatherFolderRecursive(parentSnap.ref, folderEntry);

        // ── Registro en papelera (con árbol completo para restauración) ─
        try {
          await recordToTrash(firebase, state, {
            itemType: 'folder',
            itemName: folderEntry.name,
            customerId: extractCustomerId(parentSnap.ref.path),
            breadcrumb: [folderEntry.name],
            restorePayload: {
              parentRefPath: parentSnap.ref.path,
              folderEntry: folderEntry,
              subCollectionId: folderEntry.reference?.id,
              // Mantengo el campo legacy `subCollectionDocs` (primer nivel)
              // para compatibilidad con restore antiguo.
              subCollectionDocs: descendants
                .filter(d => {
                  // Solo docs en el primer nivel: path = parent/subColId/docId
                  const parentPathLen = parentSnap.ref.path.split('/').length;
                  return d.path.split('/').length === parentPathLen + 2;
                })
                .map(d => ({ id: d.path.split('/').pop(), data: d.data })),
              // NUEVO: árbol completo (todos los descendientes con su path)
              descendants,
              legacyFiles
            }
          });
        } catch (e) { console.warn('No se pudo registrar en papelera (carpeta):', e); }

        // ── Audit log antes de borrar (snapshot del entry padre) ───────
        const uid = firebase().auth().currentUser.uid;
        const eventType = `${ DOCUMENTS.DELETE.DELETE_DOCUMENTS }${ parentSnap.ref.path.includes('templates/folders') ? '_TEMPLATES' : '' }`;
        const auditBatch = db.batch();
        const log_audit = db.collection('audit').doc();
        auditBatch.set(log_audit, {
          uid,
          eventType,
          eventDate: firebase().firestore.FieldValue.serverTimestamp()
        });
        auditBatch.set(log_audit.collection('before').doc(), {
          folderEntry,
          descendantsCount: descendants.length,
          legacyFilesCount: legacyFiles.length
        });
        await auditBatch.commit();

        // ── Borrado en batches (límite 500 ops por batch) ──────────────
        const CHUNK = 400;
        const allPaths = [
          ...descendants.map(d => d.path),
          ...legacyFiles.map(f => f.path)
        ];
        for (let i = 0; i < allPaths.length; i += CHUNK) {
          const batch = db.batch();
          allPaths.slice(i, i + CHUNK).forEach(p => batch.delete(db.doc(p)));
          await batch.commit();
        }

        // ── Quitar entry del padre ─────────────────────────────────────
        await parentSnap.ref.update({
          folders: firebase().firestore.FieldValue.arrayRemove(folderEntry)
        });

        return resolve(dispatch({
          type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
          data: item.reference.path,
          deletedCount: descendants.length + legacyFiles.length
        }));

      } catch (error) {
        console.error('Error al eliminar carpeta recursivamente:', error);
        return reject(dispatch({
          type: DOCUMENTS.DELETE.ERROR,
          code: error.code,
          message: error.message
        }));
      }
    });
  },

  /**
   * Función que permite cerrar una carpeta para un cliente especifico.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Información de la carpeta que se cierra con la referencia en la base de datos
   * allowed: true cuando lo da canCloseFolderIn (rol, users.Carpetas o la
   * matriz de empresa), que no se refleja en `roles.closing`.
   */
  close: ({ roles, item, allowed = false }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.CLOSE.CLOSING_DOCUMENTS });

    return new Promise( async ( resolve, reject ) => {
      if ( roles?.closing || allowed === true ) {
        try {

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();
          
          const fields = await item.reference.path.get();
          const folders = fields.data().folders;
          const index = folders?.findIndex( folder => folder.name === item.name );
          
          const log_audit = db.collection('audit').doc();
          const before = { ...folders[ index ] };
          
          folders[ index ].close = {
            date: new Date()
          }

          const after = { ...folders[ index ] };

          batch.set(log_audit, { uid, eventType: DOCUMENTS.UPDATE.UPDATE_DOCUMENTS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit.collection('before').doc(), before);
          batch.set(log_audit.collection('after').doc(), after);
          batch.update(fields.ref, { folders })

          await batch.commit();
          return resolve(
            dispatch({
              type: DOCUMENTS.CLOSE.CLOSED_DOCUMENTS,
              data: fields.ref
            })
          );

        } catch ( error ) {
          return reject(
            dispatch({
              type: DOCUMENTS.CLOSE.ERROR,
              code: error.code,
              message: error.message
            })
          );
        }
      } else {
        reject(
          dispatch({ type: DOCUMENTS.CLOSE.ERROR, code: 0, message: 'Permisos insuficientes para cerrar la carpeta' })
        )
      }
    })

  },

  /**
   * Función que permite abrir una carpeta para un cliente especifico.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Información de la carpeta que se abre con la referencia en la base de datos
   * allowed: true cuando lo da canOpenFolderIn (rol, users.Carpetas o la
   * matriz de empresa).
   */
  open: ({ roles, item, allowed = false }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.CLOSE.CLOSING_DOCUMENTS });

    return new Promise( async ( resolve, reject ) => {
      if ( roles?.closing || allowed === true ) {
        try {

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();
          
          const fields = await item.reference.path.get();
          const folders = fields.data().folders;
          const index = folders?.findIndex( folder => folder.name === item.name );
          
          const log_audit = db.collection('audit').doc();
          const before = { ...folders[ index ] };
          
          delete folders[ index ].close;

          const after = { ...folders[ index ] };

          batch.set(log_audit, { uid, eventType: DOCUMENTS.UPDATE.UPDATE_DOCUMENTS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit.collection('before').doc(), before);
          batch.set(log_audit.collection('after').doc(), after);
          batch.update(fields.ref, { folders })

          await batch.commit();
          return resolve(
            dispatch({
              type: DOCUMENTS.CLOSE.CLOSED_DOCUMENTS,
              data: fields.ref
            })
          );

        } catch ( error ) {
          return reject(
            dispatch({
              type: DOCUMENTS.CLOSE.ERROR,
              code: error.code,
              message: error.message
            })
          );
        }
      } else {
        reject(
          dispatch({ type: DOCUMENTS.CLOSE.ERROR, code: 0, message: 'Permisos insuficientes para abrir la carpeta' })
        )
      }
    })

  },

  /**
   * Ajusta la matriz de EMPRESA folders/{cid}/users/{uid} (spec 2026-10-07 §4).
   * Las matrices de carpeta ya no se escriben. Exige:
   *  - roles.update (rol de quien edita, sección folders);
   *  - canAdjust(editorRole, targetRole): rango(E) > rango(T); un rol objetivo
   *    desconocido (null) no se ajusta;
   *  - si cambia algún permiso de eliminar, rango(E) ≥ 60 (lib/rank, comparado
   *    con toGranular igual que las reglas).
   * carpetas: abrir/cerrar que decide la empresa (lib/closed_folder_perms). Solo
   * lo escribe root; para los demás se conserva el que ya tenía la matriz.
   */
  setPermissions: ({ roles, matrixRef, permissions, uid, editorRole, targetRole, carpetas }) => ( dispatch, state, firebase )  => {

    dispatch({ type: DOCUMENTS.UPDATE.UPDATING_DOCUMENTS });

    return new Promise( async ( resolve, reject ) => {
      const deny = ( message ) => reject( dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message }) );
      if ( !roles?.update ) return deny( 'Permisos insuficientes para actualizar los permisos' );
      const segs = String( matrixRef?.path || '' ).split('/');
      if ( !uid || originOfMatrixPath( matrixRef?.path ) !== 'empresa' || segs[3] !== uid ) {
        return deny( 'Solo se ajustan los permisos de la empresa del usuario indicado.' );
      }
      if ( !canAdjust( editorRole, targetRole ) ) {
        return deny( 'No puede ajustar a este usuario: su rango es igual o mayor al suyo.' );
      }
      try {

        const db = firebase().firestore();
        const currentUser = firebase().auth().currentUser.uid;
        const batch = db.batch();

        const log_audit = db.collection('audit').doc();
        const users = (await matrixRef.get()).data();
        const before = users ? { ...users } : null;
        if ( !canToggleDelete( editorRole ) && diffTouchesDelete( before?.permissions || null, permissions ) ) {
          return deny( 'Solo contador, supervisora o root pueden dar o quitar permisos de eliminar.' );
        }
        const after = {
          user: db.collection('users').doc( uid ),
          permissions: {
            documents: permissions.documents,
            folders: permissions.folders
          }
        };
        const nextCarpetas = editorRole?.documents?.root === true && carpetas !== undefined
          ? carpetas
          : before?.permissions?.carpetas;
        if ( nextCarpetas && Object.keys( nextCarpetas ).length ) after.permissions.carpetas = nextCarpetas;

        const audit = buildPermissionsAudit({
          eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_PERMISSIONS`,
          actorUid: currentUser,
          targetUid: uid,
          matrixPath: matrixRef.path,
          before,
        });
        batch.set(log_audit, {
          ...audit,
          eventDate: firebase().firestore.FieldValue.serverTimestamp(),
        });
        if (before) batch.set(log_audit.collection('before').doc(), before);
        batch.set(log_audit.collection('after').doc(), after);
        batch.set(matrixRef, after);

        await batch.commit();
        return resolve(
          dispatch({
            type: DOCUMENTS.UPDATE.UPDATED_DOCUMENTS,
            data: matrixRef
          })
        );

      } catch ( error ) {
        return reject(
          dispatch({
            type: DOCUMENTS.UPDATE.ERROR,
            code: error.code,
            message: error.message
          })
        );
      }
    });

  },

  /**
   * «Restablecer a los permisos del rol» del personal global (spec 2026-10-07
   * §4): borra su matriz de EMPRESA folders/{cid}/users/{uid}, con auditoría;
   * vuelve a regir su rol. Solo la matriz de empresa y solo con rango mayor.
   */
  clearCompanyPermissions: ({ roles, matrixRef, uid, editorRole, targetRole }) => ( dispatch, state, firebase ) => (
    canAdjust( editorRole, targetRole )
      ? clearMatrix({ roles, matrixRef, uid, expectedOrigin: 'empresa', keepCarpetas: editorRole?.documents?.root !== true })( dispatch, state, firebase )
      : Promise.reject( dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: 'No puede ajustar a este usuario: su rango es igual o mayor al suyo.' }) )
  ),

  /**
   * Revoca TODAS las matrices de permisos de un usuario en una empresa.
   * Recorre recursivamente folders/{cid}/.../users/{uid} y elimina cada
   * matriz encontrada. Útil cuando un usuario sale de la empresa y hay que
   * limpiar los accesos distribuidos en múltiples carpetas.
   *
   * Recibe:
   *  - roles                Roles del actor (debe tener `update` para permisos)
   *  - customerRefPath      Path del doc raíz del cliente: 'folders/{customerId}'
   *  - targetUid            UID del usuario al que se le revocan los accesos
   *  - onProgress           callback({ scanned, deleted }) para UI en vivo
   *  - signal               { aborted: boolean } para cancelar cooperativamente
   */
  revokeAllPermissions: ({ roles, customerRefPath, targetUid, onProgress, signal }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.UPDATE.UPDATING_DOCUMENTS });

    return new Promise( async ( resolve, reject ) => {
      if ( !roles?.update ) {
        return reject(
          dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: 'Permisos insuficientes para revocar accesos.' })
        );
      }
      if ( !customerRefPath || !targetUid ) {
        return reject(
          dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: 0, message: 'Faltan parámetros (customerRefPath, targetUid).' })
        );
      }

      try {
        const db = firebase().firestore();
        const currentUser = firebase().auth().currentUser.uid;
        const rootRef = db.doc(customerRefPath);

        const stats = { scanned: 0, deleted: 0 };
        const deletedPaths = [];
        // En prod hay árboles de profundidad 14: el tope queda muy por encima.
        const MAX_DEPTH = 24;

        const walk = async (docRef, depth) => {
          if (!docRef || depth > MAX_DEPTH) return;
          if (signal?.aborted) return;
          stats.scanned++;

          // Intenta eliminar la matriz si existe
          try {
            const matrixRef = docRef.collection('users').doc(targetUid);
            const existing = await matrixRef.get();
            if (existing.exists) {
              await matrixRef.delete();
              stats.deleted++;
              deletedPaths.push(matrixRef.path);
            }
            if (onProgress) onProgress({ ...stats });
          } catch (e) {
            console.warn('revokeAllPermissions: no se pudo procesar', docRef.path, e?.message);
          }

          // Recorre las sub-carpetas
          let data;
          try {
            const snap = await docRef.get();
            data = snap.exists ? snap.data() : null;
          } catch (_e) { return; }
          if (!data?.folders || !Array.isArray(data.folders)) return;

          for (const entry of data.folders) {
            if (signal?.aborted) return;
            if (!entry?.reference?.id) continue;
            try {
              const subCol = docRef.collection(entry.reference.id);
              const subSnap = await subCol.get();
              for (const childDoc of subSnap.docs) {
                if (signal?.aborted) return;
                await walk(childDoc.ref, depth + 1);
              }
            } catch (e) {
              console.warn('revokeAllPermissions: sub-coll falló', entry.reference.id, e?.message);
            }
          }
        };

        await walk(rootRef, 0);

        // Matrices propias de primer nivel (spec 2026-10-06):
        // folders/{cid}/permisos_carpetas/{X}/users/{uid}. El cliente no puede
        // listar esa colección: se recorren los ids de folders[] de la raíz
        // (las de carpetas ya borradas las limpia el borrado de carpetas).
        try {
          const rootSnap = await rootRef.get();
          const ids = [...new Set(((rootSnap.exists ? rootSnap.data()?.folders : null) || [])
            .map(f => f?.reference?.id).filter(Boolean))];
          for (const id of ids) {
            if (signal?.aborted) break;
            const ref = db.doc(`${customerRefPath}/permisos_carpetas/${id}/users/${targetUid}`);
            const snap = await ref.get();
            if (snap.exists) {
              await ref.delete();
              stats.deleted++;
              deletedPaths.push(ref.path);
            }
          }
          if (onProgress) onProgress({ ...stats });
        } catch (e) {
          console.warn('revokeAllPermissions: permisos_carpetas falló', e?.message);
        }

        // Audit resumido si se eliminó al menos una matriz
        if (stats.deleted > 0) {
          try {
            const log = db.collection('audit').doc();
            const batch = db.batch();
            batch.set(log, {
              uid: currentUser,
              eventType: 'REVOKE_ALL_DOCUMENTS_PERMISSIONS',
              eventDate: firebase().firestore.FieldValue.serverTimestamp(),
              targetUid,
              rootPath: customerRefPath,
              deletedCount: stats.deleted,
              scannedCount: stats.scanned,
              aborted: !!signal?.aborted
            });
            batch.set(log.collection('before').doc(), {
              paths: deletedPaths.slice(0, 500),
              truncated: deletedPaths.length > 500
            });
            await batch.commit();
          } catch (e) {
            console.warn('Audit de revocación falló (continuando):', e?.message);
          }
        }

        return resolve(
          dispatch({
            type: DOCUMENTS.UPDATE.UPDATED_DOCUMENTS,
            data: { deleted: stats.deleted, scanned: stats.scanned, aborted: !!signal?.aborted }
          })
        );
      } catch (error) {
        return reject(
          dispatch({ type: DOCUMENTS.UPDATE.ERROR, code: error.code, message: error.message })
        );
      }
    });

  },

  /**
   * Función que permite clonar una carpeta a partir de una plantilla.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Referencia de la carpeta en la base de datos
   * Estructura de directorios que van a clonar a partir de la plantilla
   */
  clone: ({ roles, reference, folders }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.CLONE.CLONING_DOCUMENTS });

    return new Promise( async ( resolve, reject ) => {
      if ( roles.create ) {
        try {

          const db = firebase().firestore();

          const createFolder = async () => {
            try {
              for ( const folder of folders ) {

                const id = folder.substring( folder.lastIndexOf("/") + 1, folder.length );
                const item = await db.doc( folder.substring( 0, folder.lastIndexOf("/")) ).get();
                const data = item.data().folders.filter( folder => folder.reference.id === id )[0]
      
                const doc = await dispatch( documents.create({ reference, roles,
                  folder: {
                    name: data.name,
                    description: data.description,
                    isUpload: data.isUpload,
                  }
                }));
      
                let fields = ( await doc?.data.get() );
                fields = typeof fields?.data === "function" ? fields.data()?.folders || [] : [];
                
                const ref = Array.isArray(fields) ? fields[0]?.reference.path.collection( fields[0].reference.id ) || doc?.data : doc?.data;
                await createSubFolder({ ref: item.ref.collection( id ), folder: ref })
      
              }
            } catch (error) { new Error(error) };
          };

          const createSubFolder = async ({ ref, folder }) => {
            try{

              const items = await ref.get();
              for ( const doc of items.docs ) {
                
                const folders = typeof doc?.data === "function" ? doc?.data()?.folders || [] : [];
                for ( const data of folders ) {

                  const docref = await dispatch( documents.create({ reference: folder, roles,
                    folder: {
                      name: data.name,
                      description: data.description,
                      isUpload: data.isUpload,
                    }
                  }));

                  let fields = (await docref.data.get());
                  fields = typeof fields?.data === "function" ? fields.data()?.folders || [] : [];
                  
                  const folderRef = Array.isArray(fields) ? fields[0]?.reference.path.collection( fields[0].reference.id ) || docref?.data : docref?.data;
                  await createSubFolder({ ref: data.reference.path.collection( data.reference.id ), folder: folderRef });
                  
                }
                 

              }

            } catch( error ) { new Error(error) };
          };

          await createFolder();
          resolve({ type: DOCUMENTS.CLONE.CLONED_DOCUMENTS, ref: reference });
        
        } catch (error) {
          reject(
            dispatch({ type: DOCUMENTS.CLONE.ERROR, code: error?.code, message: error?.message })
          );
        }
      } else {
        reject(
          dispatch({ type: DOCUMENTS.CLONE.ERROR, code: 0, message: 'Permisos insuficientes para clonar carpetas' })
        )
      }
    })

  }

}

/**
 * Exporta las funciones para el manejo de los archivos
 */
export const files = {

  /**
   * Función que permite eliminar un archivo de una carpeta especifica.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Referencia del archivo en la base de datos
   */
  delete: ({ roles, reference, requestedBy }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.DELETE.DELETING_DOCUMENTS });

    return new Promise( async ( resolve, reject ) => {
      if ( roles.delete ) {
        try {

          const folder = await reference.get();

          if ( !folder.exists ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'El archivo que intenta eliminar no existe'
            })
          );
          
          if ( !(folder.data().folder) ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'Estructura de archivo incorrecta'
            })
          );

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();

          // Captura datos del doc apuntado por folder.data().folder para poder restaurar
          let fileData = null;
          try {
            const folderInner = folder.data().folder;
            if (folderInner?.get) {
              const innerSnap = await folderInner.get();
              if (innerSnap.exists) fileData = innerSnap.data();
            }
          } catch (e) { console.warn('No se pudo leer doc interno para papelera:', e); }

          // Registro en papelera
          try {
            const parentData = folder.data();
            const fileRefPath = parentData?.folder?.path || null;
            await recordToTrash(firebase, state, {
              itemType: 'file-legacy',
              itemName: fileData?.name || parentData?.name || 'Archivo',
              itemExt: (fileData?.name || parentData?.name || '').split('.').pop() || null,
              customerId: extractCustomerId(reference.path),
              breadcrumb: [fileData?.name || parentData?.name || ''],
              requestedBy,
              restorePayload: {
                parentRefPath: reference.path,
                parentData,
                fileRefPath,
                fileData: fileData || {}
              }
            });
          } catch (e) { console.warn('No se pudo registrar en papelera (file-legacy):', e); }

          const log_audit = db.collection('audit').doc();

          batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.DELETE.DELETE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit.collection('before').doc(), sanitizeForFirestore(folder.data()));
          batch.delete(folder.data().folder);
          batch.delete(reference);

          // Si el archivo vive en la subcolección __files__ del doc de carpeta
          // (overflow), borrar también ese doc. folder.data().folder es el
          // doc-puntero folders/.../{name}; dos .parent llegan al doc self de
          // la carpeta. batch.delete sobre un doc inexistente es no-op, así que
          // es seguro también para archivos que solo están en el array files[].
          try {
            const stub = folder.data().folder;
            const folderSelf = stub?.parent?.parent;
            if (folderSelf && typeof folderSelf.collection === 'function') {
              batch.delete(folderSelf.collection(FILES_SUBCOLLECTION).doc(reference.id));
            }
          } catch (e) { console.warn('No se pudo encolar borrado de __files__:', e?.message); }

          await batch.commit()
          return resolve(
            dispatch({
              type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
              data: reference
            })
          );

        } catch ( error ) {
          return reject (
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: error.code,
              message: error.message
            })
          );
        }         
      } else {
        reject(
          dispatch({ type: DOCUMENTS.DELETE.ERROR, code: 0, message: 'Permisos insuficientes para eliminar la lista de archivos' })
        )
      }
    })

  },

  /**
   * Función que permite reversar UNA contabilización de un archivo.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Referencia del archivo en la base de datos
   * accountingRef: la referencia del doc contable a reversar. Opcional por
   * compatibilidad: si no llega y el archivo tiene una sola, se reversa esa.
   * El campo `accounting` admite ref suelta (legacy) o array (actual); al
   * quedar vacío se elimina del doc.
   */
  accountingThrowBack: ({ roles, reference, accountingRef, requestedBy }) => ( dispatch, state, firebase ) => {
    return new Promise( async ( resolve, reject ) => {
      if ( roles.accounting ) {
        try {

          const field = firebase().firestore.FieldValue;
          const file = await reference.get();

          if ( !file.exists ) return reject(
            dispatch({
              type: DOCUMENTS.ACCOUNTING.ERROR,
              code: 0,
              message: 'El archivo que intenta eliminar no existe'
            })
          );

          const { accounting, ...rest } = file.data();
          const list = toAccountingList( accounting );

          if ( !list.length ) return reject(
            dispatch({
              type: DOCUMENTS.ACCOUNTING.ERROR,
              code: 0,
              message: 'Estructura de archivo incorrecta'
            })
          );

          const target = accountingRef || ( list.length === 1 ? list[0] : null );
          if ( !target ) return reject(
            dispatch({
              type: DOCUMENTS.ACCOUNTING.ERROR,
              code: 0,
              message: 'Debe indicar cuál contabilización desea reversar'
            })
          );

          const remaining = removeAccountingRef( accounting, target );
          if ( remaining.length === list.length ) return reject(
            dispatch({
              type: DOCUMENTS.ACCOUNTING.ERROR,
              code: 0,
              message: 'La contabilización indicada no pertenece a este archivo'
            })
          );

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();

          const log_audit_update = db.collection('audit').doc();
          const log_audit_delete = db.collection('audit').doc();

          const after = remaining.length ? { ...rest, accounting: remaining } : rest;

          batch.set(log_audit_update, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_update.collection('before').doc(), sanitizeForFirestore({ accounting, ...rest }));
          batch.set(log_audit_update.collection('after').doc(), sanitizeForFirestore(after));

          const targetData = (await target.get()).data();
          batch.set(log_audit_delete, { uid, eventType: `${ DOCUMENTS.DELETE.DELETE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_delete.collection('before').doc(), sanitizeForFirestore(targetData));
          addAttachmentToTrash( batch, firebase, {
            kind: 'accounting', attachmentRef: target, attachmentData: targetData,
            parentRef: reference, parentData: file.data(), requestedBy,
          });

          batch.delete(target);
          batch.update(reference, { accounting: remaining.length ? remaining : field.delete() });

          await batch.commit()
          // Quitar un soporte contable cambia hasAccounting en la entrada de
          // files[]: sin esto el cliente seguiria viendo el tag Contabilizado.
          await denormalizeFileFlagsToParent( reference, firebase );
          return resolve(
            dispatch({
              type: DOCUMENTS.ACCOUNTING.ACCOUNTING_THROW_BACK,
              data: reference
            })
          );

        } catch ( error ) {
          return reject (
            dispatch({
              type: DOCUMENTS.ACCOUNTING.ERROR,
              code: error.code,
              message: error.message
            })
          );
        }
      } else {
        reject(
          dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: 0, message: 'Permisos insuficientes para reversar la contabilización' })
        )
      }
    });
  },

  /**
   * Marca manual de contabilizado (independiente del archivo contable).
   * Persiste `accounted: {at, by, byName}` en files/{id} con audit trail.
   * Solo usuarios con permiso documents.accounting.
   */
  markAccounted: ({ roles, reference }) => ( dispatch, state, firebase ) => {
    return new Promise( async ( resolve, reject ) => {
      if ( !roles?.accounting ) return reject(
        dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: 'permissions', message: 'No tiene permisos para contabilizar' })
      );
      try {
        const db = firebase().firestore();
        const user = firebase().auth().currentUser;
        const file = await reference.get();
        if ( !file.exists ) return reject(
          dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: 0, message: 'El archivo no existe' })
        );

        const before = file.data();

        // byName desde Firestore users/{uid} (fuente de verdad), NO desde el
        // displayName de Auth: ese solo se escribe al crear el usuario y si
        // el admin renombró a mitad de sesión estampaba el nombre viejo.
        // Best-effort: si la lectura falla, cae al displayName/email/uid.
        const userDoc = await db.collection('users').doc(user.uid).get()
          .catch(() => null);
        const accounted = {
          at: firebase().firestore.FieldValue.serverTimestamp(),
          by: user.uid,
          byName: canonicalUserName(userDoc?.data(), user)
        };

        const batch = db.batch();
        const log_audit = db.collection('audit').doc();
        batch.set(log_audit, { uid: user.uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('before').doc(), sanitizeForFirestore(before));
        batch.set(log_audit.collection('after').doc(), sanitizeForFirestore({ ...before, accounted }));
        batch.update(reference, { accounted });

        await batch.commit();
        await denormalizeFileFlagsToParent( reference, firebase );
        return resolve( dispatch({ type: DOCUMENTS.ACCOUNTING.ACCOUNTING_THROW_BACK, data: reference }) );
      } catch ( error ) {
        return reject( dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: error.code, message: error.message }) );
      }
    })
  },

  /**
   * Quita la marca manual de contabilizado (FieldValue.delete sobre accounted).
   */
  unmarkAccounted: ({ roles, reference }) => ( dispatch, state, firebase ) => {
    return new Promise( async ( resolve, reject ) => {
      if ( !roles?.accounting ) return reject(
        dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: 'permissions', message: 'No tiene permisos para contabilizar' })
      );
      try {
        const db = firebase().firestore();
        const user = firebase().auth().currentUser;
        const file = await reference.get();
        if ( !file.exists ) return reject(
          dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: 0, message: 'El archivo no existe' })
        );

        const { accounted, ...rest } = file.data();
        if ( !accounted ) return resolve( dispatch({ type: DOCUMENTS.ACCOUNTING.ACCOUNTING_THROW_BACK, data: reference }) );

        const batch = db.batch();
        const log_audit = db.collection('audit').doc();
        batch.set(log_audit, { uid: user.uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('before').doc(), sanitizeForFirestore({ accounted, ...rest }));
        batch.set(log_audit.collection('after').doc(), sanitizeForFirestore(rest));
        batch.update(reference, { accounted: firebase().firestore.FieldValue.delete() });

        await batch.commit();
        await denormalizeFileFlagsToParent( reference, firebase );
        return resolve( dispatch({ type: DOCUMENTS.ACCOUNTING.ACCOUNTING_THROW_BACK, data: reference }) );
      } catch ( error ) {
        return reject( dispatch({ type: DOCUMENTS.ACCOUNTING.ERROR, code: error.code, message: error.message }) );
      }
    })
  },

  /**
   * Función que permite eliminar un archivo XML vinculado en una carpeta especifica.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Referencia del archivo en la base de datos
   */
  deleteXML: ({ roles, reference, expectedPath, requestedBy }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.DELETE.DELETING_DOCUMENTS })
    return new Promise( async ( resolve, reject ) => {
      // Permiso granular (spec 2026-10-01). Se normaliza lo que llega: al aprobar
      // una solicitud, Solicitudes pasa el rol GLOBAL tal cual (sin deleteXml),
      // y en un rol viejo deleteXml hereda de delete.
      if ( toGranular({ documents: roles || {} }).documents.deleteXml ) {
        try {

          const field = firebase().firestore.FieldValue;
          const file = await reference.get();
          const { xml, ...rest } = file.data();

          if ( !file.exists ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'El archivo que intenta eliminar no existe'
            })
          );
          
          if ( !xml ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'Estructura de archivo incorrecta'
            })
          );

          // Al aprobar una solicitud, el XML pedido pudo haber sido reemplazado
          // por otro: este borrado toma el XML ACTUAL del documento, así que sin
          // esta guarda borraría el nuevo (ver actions/database/deletion_requests.js).
          if ( expectedPath && xml.path !== expectedPath ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'Estructura de archivo incorrecta'
            })
          );

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();

          const log_audit_update = db.collection('audit').doc();
          const log_audit_delete = db.collection('audit').doc();

          batch.set(log_audit_update, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_update.collection('before').doc(), sanitizeForFirestore({ xml, ...rest }));
          batch.set(log_audit_update.collection('after').doc(), sanitizeForFirestore(rest));

          const xmlData = (await xml.get()).data();
          batch.set(log_audit_delete, { uid, eventType: `${ DOCUMENTS.DELETE.DELETE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_delete.collection('before').doc(), sanitizeForFirestore(xmlData));
          addAttachmentToTrash( batch, firebase, {
            kind: 'xml', attachmentRef: xml, attachmentData: xmlData,
            parentRef: reference, parentData: file.data(), requestedBy,
          });

          batch.delete(xml);
          batch.update(reference, { xml: field.delete() });

          await batch.commit()
          return resolve(
            dispatch({
              type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
              data: reference
            })
          );

        } catch ( error ) {
          return reject (
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: error.code,
              message: error.message
            })
          );
        }
      } else {
        reject(
          dispatch({ type: DOCUMENTS.DELETE.ERROR, code: 0, message: 'Permisos insuficientes para eliminar archivo XML' })
        )
      }
    });
  },

  /**
   * Función que permite eliminar un archivo vinculado en una carpeta especifica.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Referencia del archivo en la base de datos
   * Referencia del archivo vinculado
   */
  deleteLink: ({ roles, reference, link, requestedBy }) => ( dispatch, state, firebase ) => {

    dispatch({ type: DOCUMENTS.DELETE.DELETING_DOCUMENTS })
    return new Promise( async ( resolve, reject ) => {
      // Permiso granular (spec 2026-10-01); el gate real es canDeleteLinked en
      // la pantalla, y FULL_PERMS no trae deleteLinked: por eso también delete.
      if ( roles.deleteLinked || roles.delete ) {
        try {

          const field = firebase().firestore.FieldValue;
          const file = await reference.get();
          const { links, ...rest } = file.data();

          if ( !file.exists ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'El archivo que intenta eliminar no existe'
            })
          );
          
          if ( !links ) return reject(
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: 0,
              message: 'Estructura de archivo incorrecta'
            })
          );

          const db = firebase().firestore();
          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();

          const log_audit_update = db.collection('audit').doc();
          const log_audit_delete = db.collection('audit').doc();

          const before = { links: Object.assign([], links), ...rest };

          // Por ruta, no por identidad: `link` nunca es el mismo objeto que el
          // del arreglo, así que indexOf daba -1 y splice(-1) quitaba el
          // ÚLTIMO vinculado de la foto "después" del audit. El borrado real
          // siempre estuvo bien (arrayRemove compara por valor).
          const linkIdx = links.findIndex( l => l?.path === link?.path );
          if ( linkIdx >= 0 ) links.splice( linkIdx, 1 );
          const after = { links, ...rest };

          batch.set(log_audit_update, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_update.collection('before').doc(), sanitizeForFirestore(before));
          batch.set(log_audit_update.collection('after').doc(), sanitizeForFirestore(after));

          const linkData = (await link.get()).data();
          batch.set(log_audit_delete, { uid, eventType: `${ DOCUMENTS.DELETE.DELETE_DOCUMENTS }_FILES`, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_delete.collection('before').doc(), sanitizeForFirestore(linkData));
          addAttachmentToTrash( batch, firebase, {
            kind: 'link', attachmentRef: link, attachmentData: linkData,
            parentRef: reference, parentData: file.data(), requestedBy,
          });

          batch.delete(link);
          batch.update(reference, { links: field.arrayRemove( link ) });

          await batch.commit()
          return resolve(
            dispatch({
              type: DOCUMENTS.DELETE.DELETED_DOCUMENTS,
              data: reference
            })
          );

        } catch ( error ) {
          return reject (
            dispatch({
              type: DOCUMENTS.DELETE.ERROR,
              code: error.code,
              message: error.message
            })
          );
        }
      } else {
        reject(
          dispatch({ type: DOCUMENTS.DELETE.ERROR, code: 0, message: 'Permisos insuficientes para eliminar archivo XML' })
        )
      }
    });    

  },

  /**
   * Función que permite descargar un archivo en una carpeta especifica.
   * Recibe como parametros:
   * URL del archivo
   * Nombre del archivo
   */
  download: ( url, fileName ) => async( dispatch, state, firebase ) => {
  
    const storage = firebase().storage();
  
    
  
    storage.refFromURL( url ).getDownloadURL().then( url => {
      fetch( url ).then( res => res.blob() ).then( blob => {
        
        const url = URL.createObjectURL( blob );
        const link = document.createElement('a');

        document.body.appendChild( link );

        link.href = url;
        link.download = fileName;

        if( window.navigator.msSaveOrOpenBlob ) {
          window.navigator.msSaveOrOpenBlob( blob, fileName );
        } else {
          link.click();
        }
        
        document.body.removeChild( link );

        URL.revokeObjectURL( url );
        
      });
    });
    
  }
  
}

/**
 * Exporta la función que ejecuta el buscador para encontrar los archivos en la base de datos
 */
export const searcher = ({ roles, fields }) => ( dispatch, state, firebase ) => {

  // console.log(`Hora de inicio: ${ moment().format('HH:mm:ss') }`);
  dispatch({ type: DOCUMENTS.SEARCHER.SEARCHING_DOCUMENTS });

  return new Promise( async ( resolve, reject ) => {
    if ( roles.read ) {
      try {

        const files = [];
        const db = firebase().firestore();
        const permissions = state().get('roles').get('roles').roles.documents;
        const user = state().get('authenticate').get('login').user;

        const customers = fields.customer[0] === "-1"
          ? permissions.level === "all" 
            ? ( await db.collection('customers').get() ).docs.map( doc => doc.data()['Razon social'] )
            : ( await db.collection('customers').get() ).docs
                // Pertenencia por uid (Usuarios[] o UsuariosUids): el
                // array-contains sobre el objeto {email, reference} no
                // encontraba las entradas con isAdmin (spec 2026-10-05).
                .filter( doc => isMember( doc.data(), user.uid ) )
                .map( doc => doc.data()['Razon social'] )
          : fields.customer.map( customer => customer.split("/")[1] );
        
        for ( const customer of customers.sort() ) {

          let collection = db.collection('files').where( 'customer', '==', customer );
          if ( fields.reference || fields.referencevalue ) {

            const name = fields.reference ? fields.reference : ''
            let value = fields.referencevalue ? fields.referencevalue : '';
            
            if ( typeof value === 'object' ) {
              value = value.toDate();
              value.setHours(0);
              value.setMinutes(0);
              value.setSeconds(0);
              value.setMilliseconds(0);
            }
  
            collection = collection.where("references", "array-contains", { name, value });
  
          }
  
          if ( Array.isArray( fields.date_of_upload ) ) {
            
            let date = { start: fields.date_of_upload[0].toDate(), end: fields.date_of_upload[1].toDate() };
  
            date.start.setHours(0);
            date.start.setMinutes(0);
            date.start.setSeconds(0);
            date.start.setMilliseconds(0);
  
            date.end.setHours(23);
            date.end.setMinutes(59);
            date.end.setSeconds(59);
            date.end.setMilliseconds(999);
  
            collection = collection.where("date_of_upload", ">=", date.start).where("date_of_upload", "<=", date.end);          
  
          }

          if ( fields.filename ) collection = collection.where("name", "==", fields.filename);
  
          const query = await collection.get();

          if ( query.docs.length > 0 ) {
            for ( const doc of query.docs ) {

              if ( Array.isArray( fields.approve ) ) {

                let exists = true;
                if ( Array.isArray( doc.data().approve ) ) {
                  fields.approve.forEach( email => {
                    exists = doc.data().approve.includes( email.split("/")[1] );
                  })
                } else exists = false
            
                if ( exists ) files.push({
                  ref: doc.ref,
                  data: doc.data()
                });
            
              } else files.push({
                ref: doc.ref,
                data: doc.data()
              });

            };
          }

        }

        // Filtro (spec 2026-10-07): lo que el usuario no puede leer en la
        // empresa, o está bajo una carpeta restringida para él, no aparece.
        // La empresa se lee una vez por búsqueda. Root ve todo.
        const roleState = state().get('roles').get('roles').roles;
        const isRootUser = roleState?.documents?.root === true;
        const accessCache = new Map();
        const accessFor = ( cid ) => {
          if ( !accessCache.has( cid ) ) {
            accessCache.set( cid, loadCompanyAccess({ db, cid, uid: user.uid, isRootUser, readsAll: canRestrict( roleState ) }) );
          }
          return accessCache.get( cid );
        };
        const customerCache = new Map();
        const customerOf = ( cid ) => {
          if ( !customerCache.has( cid ) ) customerCache.set( cid, db.collection('customers').doc( cid ).get().then( s => ( s.exists ? s.data() : null ) ).catch( () => null ) );
          return customerCache.get( cid );
        };
        const loader = createEffectiveLoader({ db, uid: user.uid, role: roleState, isRootUser, accessFor, customerOf });
        const { kept, hidden } = await filterSearchResults({ results: files, loader, isRootUser });
        if ( hidden > 0 ) console.info(`[searcher] ${ hidden } resultado(s) ocultos por permisos o restricciones`);
        resolve( kept );

      } catch( error ) { reject(
        dispatch({
          type: DOCUMENTS.SEARCHER.ERROR,
          code: error.code,
          message: error.message
        })
      )}
    }
  });

}